import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import getMessageModalBranch from '../../helpers/get-branch-modal-message';
import IApproveOrReproveBranchModalProps from '../../interfaces/IApproveOrReproveBranchModalProps';
import ReproveBranchService from './reprove-branch-modal.service';
import ProcessingError from '../../../../common/errors/processing-error';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import {
    IGetBranchModalMessage,
    ModalAction,
} from '../../helpers/interfaces/IGetBranchModalMessage';

function ReproveBranch({
    opened,
    branches,
    closeModalCallback,
    carrierId,
    fetchBranchesData,
}: IApproveOrReproveBranchModalProps) {
    const { setToast } = useToast();
    const getSelectedBranches = useCallback(
        () => branches.filter(b => b.checked),
        [branches],
    );
    const [btnLoading, setBtnLoading] = useState<boolean>(false);
    const { t } = useTranslation();

    const closeReproveModal = useCallback(() => {
        if (!btnLoading) {
            closeModalCallback();
        }
    }, [btnLoading, closeModalCallback]);

    const reproveConfirmedCallback = useCallback(() => {
        const branchesToReproveChecked = getSelectedBranches();

        setBtnLoading(true);

        ReproveBranchService.ReproveBranch(branchesToReproveChecked, carrierId)
            .then(() => {
                setToast(
                    'positive',
                    branchesToReproveChecked.length === 1
                        ? t('FilialReprovadaSucesso')
                        : t('FilialReprovadasSucesso'),
                );
            })
            .catch(error => {
                if (error instanceof ProcessingError) {
                    setToast(
                        'warning',
                        t('FilialReprovadaParcialErro'),
                        error.message,
                    );
                } else if (error instanceof Error) {
                    setToast(
                        'negative',
                        branchesToReproveChecked.length === 1
                            ? t('FilialReprovadaErro')
                            : t('FilialReprovadasErro'),
                        error.message,
                    );
                }
            })
            .finally(() => {
                fetchBranchesData();
                setBtnLoading(false);
                closeModalCallback();
            });
    }, [
        carrierId,
        closeModalCallback,
        fetchBranchesData,
        getSelectedBranches,
        setToast,
        t,
    ]);

    const modalMessage = useMemo(() => {
        const selectedBranches = getSelectedBranches();
        const reproveMessage = getMessageModalBranch({
            branches: selectedBranches,
            action: ModalAction.reprove,
        } as IGetBranchModalMessage);

        const splitedApproveMessage = reproveMessage.split('**');

        return (
            <>
                {selectedBranches.length > 1
                    ? t('ReprobateBranches')
                    : t('ReprobateBranch')}
                <b>{splitedApproveMessage[1]}</b>
            </>
        );
    }, [getSelectedBranches, t]);

    const modalTitle = useMemo(() => {
        const selectedBranches = getSelectedBranches();

        return selectedBranches.length > 1
            ? t('FilialPluralMaisculo')
            : t('FilialMaisculo');
    }, [getSelectedBranches, t]);

    const renderCustomBody = () => (
        <span>
            {t('ReprobateMessage')}
            {modalMessage}.
        </span>
    );

    return (
        <ClbModal
            title={`${t('Reprobate')} ${modalTitle}?`}
            open={opened}
            onClose={() => closeReproveModal()}
            customBody={renderCustomBody}
            type="danger"
            data-testid="reprove-branch-modal"
            itemsFooter={[
                {
                    key: 'reprove-button',
                    id: 'reprove-button',
                    label: `${t('Reprobate')} ${modalTitle}`,
                    disabled: false,
                    loading: btnLoading,
                    title: 'Realizar reprovação',
                    'data-testid': 'modal-reprove-button',
                    onClick: () => reproveConfirmedCallback(),
                    role: 'button',
                    size: 'md',
                    styleType: 'danger',
                },
                {
                    key: 'reprove-branch-cancel-button',
                    id: 'reprove-branch-cancel-button',
                    label: t('Cancelar'),
                    disabled: btnLoading,
                    loading: false,
                    title: 'Cancelar reprovação',
                    'data-testid': 'reprove-branch-cancel-button',
                    onClick: () => closeModalCallback(),
                    role: 'button',
                    size: 'md',
                    styleType: 'secondary',
                },
            ]}
        />
    );
}

export default ReproveBranch;
